
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchCandidates } from "../store/candidates.thunk";
import { fetchDashboardStats } from "../store/dashboardThunk";

import type {
  CandidateListItem,
  HRDashboardData,
} from "../types";

import { mapDashboardStats } from "../utils/dashboardHelpers";

interface CandidateFilters {
  search: string;
  department: string;
  status: string;
  minATSScore: string;
}

const DEFAULT_CANDIDATE_FILTERS: CandidateFilters = {
  search: "",
  department: "All",
  status: "All",
  minATSScore: "",
};

export default function useHRDashboard() {
  const dispatch: any = useDispatch();

  const [candidateFilters, setCandidateFilters] =
    useState<CandidateFilters>(DEFAULT_CANDIDATE_FILTERS);

  const {
    candidates,
    pagination,
    loading: candidatesLoading,
    error: candidatesError,
  } = useSelector(
    (state: any) => state.candidates
  ) as {
    candidates: CandidateListItem[];
    pagination: any;
    loading: boolean;
    error: string | null;
  };

  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError,
  } = useSelector(
    (state: any) => state.hrDashboard
  ) as {
    data: HRDashboardData | null;
    loading: boolean;
    error: string | null;
  };

  const { user } = useSelector(
    (state: any) => state.auth
  );

  const loadCandidates = useCallback(
    (page: number, filters: CandidateFilters = candidateFilters) => {
      dispatch(
        fetchCandidates({
          page,
          limit: 5,
          search: filters.search,
          department: filters.department,
          status: filters.status,
          minATSScore: filters.minATSScore,
        })
      );
    },
    [dispatch, candidateFilters]
  );

  // Initial data load. Keep this separate from loadCandidates so
  // changing filter state does not trigger an additional automatic fetch.
  useEffect(() => {
    loadCandidates(1, DEFAULT_CANDIDATE_FILTERS);

    if (!dashboardData && !dashboardLoading) {
      dispatch(fetchDashboardStats());
    }
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleCandidateFiltersChange = useCallback(
    (filters: CandidateFilters) => {
      setCandidateFilters(filters);

      // Every filter change starts from page 1.
      loadCandidates(1, filters);
    },
    [loadCandidates]
  );

  const handleCandidatePageChange = useCallback(
    (page: number) => {
      if (page < 1) return;

      if (pagination?.totalPages && page > pagination.totalPages) {
        return;
      }

      if (page === pagination?.currentPage) return;

      // Retain the currently applied filters when changing pages.
      loadCandidates(page, candidateFilters);
    },
    [
      loadCandidates,
      candidateFilters,
      pagination?.currentPage,
      pagination?.totalPages,
    ]
  );

  const dashboardStats = useMemo(
    () => mapDashboardStats(dashboardData),
    [dashboardData]
  );

  const voiceCallLogs = dashboardData?.voiceCalls?.recent ?? [];

  return {
    user,

    candidates,
    pagination,
    candidatesLoading,
    candidatesError,
    handleCandidatePageChange,
    handleCandidateFiltersChange,

    dashboardData,
    dashboardLoading,
    dashboardError,

    dashboardStats,
    voiceCallLogs,
  };
}
